/**
 * WHERE THE WORK ACTUALLY HAPPENED.
 *
 * California requires a MOBILE repair invoice to carry the address the
 * repairs were performed at, or, where there is no address, a description
 * of the place good enough to find it again — a street and cross street, a
 * landmark, a mile marker, a freeway reference. It is in the January 2026
 * revision of the Bureau's Write It Right guide, and it bites here harder
 * than almost anywhere: 1,724 of this shop's jobs are mobile and 72 are not.
 *
 * WHY THIS IS A MODULE AND NOT A LINE IN THE INVOICE BUILDER.
 *
 * src/pages/job.ts already resolves this for the screen, and the comment
 * above it explains why it is a read-out rather than a field. Writing the
 * same resolution a second time inside the invoice is how `recalcJobTotals`
 * ended up existing in four places, each subtly different. One rule, two
 * callers.
 *
 * WHAT IT WILL NOT DO IS GUESS. When there is no address anywhere it
 * returns null and the invoice prints nothing. A made up location on a
 * document that exists to be produced in a dispute is worse than a missing
 * one, and 45 mobile jobs on the book genuinely have no location recorded.
 */

export type WhereSource = "job" | "customer" | "shop";

export type Where = {
  /** The address, exactly as somebody typed it. Never normalized. */
  address: string;
  /**
   * Which field answered. The invoice does not print this, but the job
   * screen does, and a caller that wants to nag about a weak answer needs
   * to know the difference between a recorded location and a fallback.
   */
  source: WhereSource;
  /** True when this came from the job's own recorded location. */
  recorded: boolean;
};

export type WhereInput = {
  service_at?: string | null;
  location?: string | null;
  customerAddress?: string | null;
  shopAddress?: string | null;
};

const clean = (v: unknown): string | null => {
  const s = String(v ?? "").trim();
  return s ? s : null;
};

/**
 * Resolve where a job was carried out.
 *
 * - `shop` means they came to us, so it is the shop's own address and the
 *   job's location column is not consulted. An in-shop job that somehow
 *   carries a location is still an in-shop job.
 * - `mobile`, and `null` which is 106 historical jobs nobody answered for,
 *   both walk the same chain: what intake recorded, then the customer's
 *   address, then nothing.
 */
export function whereItWasDone(i: WhereInput): Where | null {
  const at = clean(i.service_at)?.toLowerCase() ?? null;

  if (at === "shop") {
    const shop = clean(i.shopAddress);
    return shop ? { address: shop, source: "shop", recorded: true } : null;
  }

  const onTheJob = clean(i.location);
  if (onTheJob) return { address: onTheJob, source: "job", recorded: true };

  const cust = clean(i.customerAddress);
  if (cust) return { address: cust, source: "customer", recorded: false };

  return null;
}

/**
 * What to call it on a customer facing document.
 *
 * An in-shop job says "Work carried out at" because the customer drove
 * there and knows. A mobile job says "Service location", which is the
 * Bureau's own phrase and the one an inspector is looking for.
 */
export function whereLabel(w: Where | null): string {
  return w?.source === "shop" ? "Work carried out at" : "Service location";
}

/**
 * Jobs whose invoice would go out with no location on it at all.
 *
 * A list rather than a boolean, for the same reason partsMissingCondition
 * returns one: "these four jobs need an address" is work somebody can do.
 */
export function jobsMissingLocation<T extends WhereInput>(jobs: readonly T[]): T[] {
  return jobs.filter((j) => whereItWasDone(j) === null);
}
